import React, { useState, useEffect } from 'react';
import { Edit2, Trash2, MessageCircle } from 'lucide-react';
import { DeliverableComment } from '../types/deliverable';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface DeliverableCommentThreadProps {
  deliverableId: string;
  currentUserId: string;
}

export const DeliverableCommentThread: React.FC<DeliverableCommentThreadProps> = ({
  deliverableId,
  currentUserId,
}) => {
  const [comments, setComments] = useState<DeliverableComment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newContent, setNewContent] = useState('');
  const [isPosting, setIsPosting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [error, setError] = useState<string | null>(null);

  const getToken = () =>
    localStorage.getItem('authToken') || localStorage.getItem('token');

  useEffect(() => {
    if (!deliverableId) return;

    const fetchComments = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${API_BASE_URL}/deliverables/${deliverableId}/comments`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        if (!response.ok) throw new Error('Failed to fetch comments');
        const result = await response.json();
        setComments(result.data || result || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load comments');
      } finally {
        setIsLoading(false);
      }
    };

    fetchComments();
  }, [deliverableId]);

  const handlePost = async () => {
    if (!newContent.trim()) return;
    setIsPosting(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_BASE_URL}/deliverables/${deliverableId}/comments`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ content: newContent.trim() }),
        }
      );
      if (!response.ok) throw new Error('Failed to post comment');
      const result = await response.json();
      const created: DeliverableComment = result.data || result;
      setComments((prev) => [...prev, created]);
      setNewContent('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to post comment');
    } finally {
      setIsPosting(false);
    }
  };

  const handleStartEdit = (comment: DeliverableComment) => {
    setEditingId(comment.id);
    setEditContent(comment.content);
  };

  const handleSaveEdit = async (commentId: string) => {
    if (!editContent.trim()) return;
    try {
      const response = await fetch(
        `${API_BASE_URL}/deliverables/${deliverableId}/comments/${commentId}`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ content: editContent.trim() }),
        }
      );
      if (!response.ok) throw new Error('Failed to update comment');
      const result = await response.json();
      const updated: DeliverableComment = result.data || result;
      setComments((prev) =>
        prev.map((c) => (c.id === commentId ? updated : c))
      );
      setEditingId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update comment');
    }
  };

  const handleDelete = async (commentId: string) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/deliverables/${deliverableId}/comments/${commentId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      if (!response.ok) throw new Error('Failed to delete comment');
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete comment');
    }
  };

  if (isLoading) {
    return <div className="py-4 text-sm text-gray-500 text-center">Loading...</div>;
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-lg">{error}</div>
      )}

      {comments.length === 0 ? (
        <div className="flex flex-col items-center py-6 text-gray-400">
          <MessageCircle className="w-8 h-8 mb-2" />
          <p className="text-sm">No comments yet. Be the first to comment.</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {comments.map((comment) => (
            <li key={comment.id} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-800">
                    {comment.author?.name || 'Unknown'}
                  </span>
                  <span className="text-xs text-gray-400">
                    {new Date(comment.createdAt).toLocaleString()}
                  </span>
                  {comment.isEdited && (
                    <span className="text-xs text-gray-400">(edited)</span>
                  )}
                </div>
                {comment.authorId === currentUserId && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleStartEdit(comment)}
                      className="p-1 text-gray-400 hover:text-indigo-600 rounded"
                      title="Edit comment"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(comment.id)}
                      className="p-1 text-gray-400 hover:text-red-600 rounded"
                      title="Delete comment"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>

              {editingId === comment.id ? (
                <div className="space-y-2">
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={2}
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSaveEdit(comment.id)}
                      className="px-3 py-1 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="px-3 py-1 bg-gray-200 text-gray-700 text-xs rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-700">{comment.content}</p>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Add comment */}
      <div className="space-y-2">
        <textarea
          value={newContent}
          onChange={(e) => setNewContent(e.target.value)}
          placeholder="Add a comment..."
          rows={2}
          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
        />
        <button
          onClick={handlePost}
          disabled={!newContent.trim() || isPosting}
          className="px-4 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isPosting ? 'Posting...' : 'Post'}
        </button>
      </div>
    </div>
  );
};

export default DeliverableCommentThread;
